"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Share2, Plus, MoreVertical, Smartphone } from "lucide-react";

function StepNumber({ n }: { n: number }) {
  return (
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700">
      {n}
    </span>
  );
}

const STORAGE_KEY = "noread_pwa_install_dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export default function PWAInstallPrompt() {
  const pathname = usePathname() ?? "";
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (pathname === "/") {
      return;
    }

    const alreadyDismissed = localStorage.getItem(STORAGE_KEY);
    if (alreadyDismissed) return;

    // Déjà installée comme PWA ?
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const timeout = window.setTimeout(() => {
      setIsIos(ios);

      if (ios) {
        // Sur iOS, on ne peut pas déclencher l'install nativement — on guide juste l'user
        setShow(true);
      }
    }, 0);

    if (ios) {
      return () => window.clearTimeout(timeout);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [pathname]);

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  }

  async function install() {
    if (!deferredPrompt) {
      dismiss();
      return;
    }
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      dismiss();
    }
  }

  if (!show || pathname === "/") return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
      <div className="w-full max-w-sm rounded-3xl border border-violet-200 bg-white p-5 shadow-[0_8px_40px_rgba(139,92,246,0.22)]">
        <div className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/icons/icon-192-v021.png"
            alt="Noread"
            className="h-14 w-14 shrink-0 rounded-2xl"
          />
          <div>
            <p className="font-semibold text-slate-900">Installe Noread</p>
            <p className="text-xs text-slate-500">Accès rapide · Notifications · Mode offline</p>
          </div>
        </div>

        {isIos ? (
          <ol className="mt-4 flex flex-col gap-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={1} />
              <span>Appuie sur le bouton <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium"><Share2 className="h-3.5 w-3.5" strokeWidth={2} /> Partager</span> en bas du navigateur</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={2} />
              <span>Fais défiler et appuie sur <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Sur l&apos;écran d&apos;accueil</span></span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={3} />
              <span>Appuie sur <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-700">Ajouter</span> en haut à droite</span>
            </li>
          </ol>
        ) : (
          <ol className="mt-4 flex flex-col gap-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={1} />
              <span>Appuie sur le bouton <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium"><MoreVertical className="h-3.5 w-3.5" strokeWidth={2} /> Menu</span> en haut à droite</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={2} />
              <span>Appuie sur <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 font-medium"><Smartphone className="h-3.5 w-3.5" strokeWidth={2} /> Ajouter à l&apos;écran d&apos;accueil</span></span>
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <StepNumber n={3} />
              <span>Appuie sur <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-700">Ajouter</span> pour confirmer</span>
            </li>
          </ol>
        )}

        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="flex-1 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Plus tard
          </button>
          {!isIos && (
            <button
              type="button"
              onClick={install}
              className="flex-1 rounded-2xl bg-violet-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-violet-700"
            >
              Installer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
